import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, CheckCircle2, ChevronDown, ExternalLink, Loader2, Sparkles, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { FactStatus, PageHeader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { PublishAll, RefreshBar } from "@/pages/admin/RefreshBar";

const WHY = {
  DISPUTED: "Approved sources disagree. Publish to show it on Facts labelled Disputed, or Reject to keep it off Middle.",
};
const why = (s) => WHY[s] || "Checked from a manually added item. Publish it with its label, or Reject it.";

const DecisionCard = ({ f, review, verify, busy }) => (
  <div className="surface p-5" data-testid={`pending-item-${f.id}`}>
    <div className="flex flex-wrap items-center gap-2"><FactStatus status={f.status} /><span className="text-xs text-slate-500">{f.category && `${f.category} · `}{f.source_name} · {f.corroborating.length} corroborating · {f.conflicting.length} conflicting · checked {timeAgo(f.checked_at)}</span></div>
    <p className="mt-2 font-serif text-xl">{f.headline}</p>
    <p className="mt-1 text-sm text-slate-600">{f.summary}</p>
    <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`decision-why-${f.id}`}><b>Your call:</b> {why(f.status)}</p>
    <div className="mt-4 flex flex-wrap gap-2">
      <Button size="sm" className="rounded-full bg-[hsl(var(--facts))] hover:bg-[hsl(var(--facts))]/90" data-testid={`publish-${f.id}`} onClick={() => review(f.id, "publish")}>Publish{f.status === "DISPUTED" ? " as Disputed" : ""}</Button>
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`reject-${f.id}`} onClick={() => review(f.id, "reject")}>Reject</Button>
      <Link to={`/facts/${f.id}`} data-testid={`evidence-${f.id}`}><Button size="sm" variant="ghost">View evidence</Button></Link>
      <Button size="sm" variant="ghost" disabled={busy} data-testid={`reverify-${f.id}`} onClick={() => verify(f.source_record_id)}>{busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" />Checking…</> : "Re-run check"}</Button>
    </div>
  </div>
);

const RecheckDisputed = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/facts/recheck-disputed"); toast.success(`Re-checking ${data.queued} Disputed item${data.queued === 1 ? "" : "s"}. Ones that only differ in minor details will be published with a note.`); setTimeout(onDone, 20000); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={run} data-testid="recheck-disputed-btn">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Re-check Disputed</Button>;
};

const Auto = ({ title, count, testid, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="surface" data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center gap-3 p-4 text-left" data-testid={`${testid}-toggle`}>
        <span className="flex-1 text-sm font-medium">{title} <span className="text-slate-500">({count})</span></span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="divide-y divide-border border-t border-border">{children}</div>}
    </div>
  );
};

export default function AdminVerification() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const load = () => api.get("/admin/verification-queue").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner />;

  const verify = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try { const { data: r } = await api.post(`/admin/records/${id}/verify`); r.error ? toast.error(`AI verification failed: ${r.error}`) : toast.success(`Status: ${r.status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy((b) => ({ ...b, [id]: false }));
  };
  const review = async (id, action) => {
    try { await api.post(`/admin/facts/${id}/${action}`); toast.success(action === "publish" ? "Published to Facts" : "Rejected"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const n = data.decide.length;

  return (
    <div className="space-y-8" data-testid="admin-verification-page">
      <PageHeader env="admin" title="Facts verification" subtitle="The AI checks every new item and publishes clear results by itself. You only decide the items listed under 'Needs your decision'." />

      <section className={`surface flex items-start gap-4 p-5 ${n ? "border-amber-300 bg-amber-50/60" : "border-emerald-200 bg-emerald-50/60"}`} data-testid="admin-todo">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${n ? "bg-amber-500" : "bg-emerald-600"} text-white`}>{n ? <UserCheck size={20} /> : <CheckCircle2 size={20} />}</span>
        <div>
          <p className="font-serif text-2xl" data-testid="admin-todo-title">{n ? `${n} item${n === 1 ? "" : "s"} need${n === 1 ? "s" : ""} your decision` : "Nothing needs you right now"}</p>
          <p className="mt-1 text-sm text-slate-600">{n ? "Each one shows why it's waiting. Publish or Reject it. Everything else is handled automatically." : "The AI is handling the news on its own. Check back later, or look below to see what it's doing."}</p>
        </div>
      </section>

      <section className="space-y-3" data-testid="decide-section">
        <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-2xl">Needs your decision ({n})</h2>
          <div className="flex flex-wrap gap-2">{data.decide.some((f) => f.status === "DISPUTED") && <RecheckDisputed onDone={load} />}{n > 1 && <PublishAll pending={n} onDone={load} />}</div></div>
        {data.decide.map((f) => <DecisionCard key={f.id} f={f} review={review} verify={verify} busy={busy[f.source_record_id]} />)}
      </section>

      <section className="space-y-3" data-testid="auto-section">
        <h2 className="flex items-center gap-2 text-2xl"><Bot size={20} className="text-slate-400" />Handled automatically</h2>
        <p className="text-sm text-slate-500">No action needed. Shown so you can see what the AI is doing.</p>
        <RefreshBar onDone={load} />
        <Auto title="Waiting for the AI to check" count={data.ai_waiting} testid="auto-waiting">
          <p className="p-4 text-xs text-slate-500">Newest first, within the daily AI limit. Items not reached within {data.max_age_days} days are dropped as old news ({data.expired_total} so far).</p>
          {data.ai_next.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`record-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{r.title}</p>
                <p className="text-xs text-slate-500">{r.source_name} · ingested {timeAgo(r.ingested_at)}{r.verification_state === "error" && <span className="text-rose-600"> · last attempt failed</span>}</p>
              </div>
              {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-700" aria-label="Open original"><ExternalLink size={15} /></a>}
              <Button size="sm" variant="ghost" className="rounded-full" disabled={busy[r.id]} data-testid={`verify-${r.id}`} onClick={() => verify(r.id)}>
                {busy[r.id] ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}{busy[r.id] ? "Checking…" : "Check now"}
              </Button>
            </div>
          ))}
        </Auto>
        <Auto title="Closed by the AI, not published" count={data.closed_total} testid="auto-closed">
          <p className="p-4 text-xs text-slate-500">Insufficient evidence, opinion or rejected results. They stay off Facts and don't need you. Publish one anyway if you think it should be shown.</p>
          {data.closed.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`closed-item-${f.id}`}>
              <div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><FactStatus status={f.status} /><span className="text-xs text-slate-500">{f.source_name} · closed {timeAgo(f.closed_at)}</span></div>
                <Link to={`/facts/${f.id}`} className="mt-1 block text-sm font-medium hover:underline">{f.headline}</Link></div>
              {f.status !== "REJECTED" && <Button size="sm" variant="ghost" className="rounded-full" data-testid={`closed-publish-${f.id}`} onClick={() => review(f.id, "publish")}>Publish anyway</Button>}
            </div>
          ))}
        </Auto>
      </section>
    </div>
  );
}
